import React, { useEffect, useRef, useState } from 'react';

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import GroupAddIcon from '@material-ui/icons/GroupAdd';
import Alert from '@material-ui/lab/Alert';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useSnackbar } from 'notistack';

import { api, getListAPI } from '../../services';
import { PRIMARY_COLOR, STATUS_ATIVO } from '../../utils/constants';

const MOTIVO_PADRAO = 'Colaborador se recusou a assinar.';

// O RH troca a forma de assinatura de um documento pendente: 2 a 5 testemunhas
// assinam no lugar do colaborador. `documento` é a linha da lista (null = fechado).
const DialogoTestemunhas = ({ documento, onClose, onSalvo }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [opcoes, setOpcoes] = useState([]);
  const [termo, setTermo] = useState('');
  const [testemunhas, setTestemunhas] = useState([]);
  const [motivo, setMotivo] = useState(MOTIVO_PADRAO);
  const [salvando, setSalvando] = useState(false);
  // Ignora cliques repetidos antes de o estado `salvando` refletir na tela.
  const salvandoRef = useRef(false);

  useEffect(() => {
    if (!documento) return;
    getListAPI(
      'colaboradores',
      ['nome', 'asc'],
      [1, 10],
      { nome: termo, status: STATUS_ATIVO, is_user: true },
      [],
    ).then(({ data }) => setOpcoes(data));
  }, [documento, termo]);

  function fechar() {
    setTestemunhas([]);
    setTermo('');
    setMotivo(MOTIVO_PADRAO);
    onClose();
  }

  async function handleSalvar() {
    if (salvandoRef.current) return;
    salvandoRef.current = true;
    setSalvando(true);
    try {
      const response = await api.put(
        `/documentos/${documento.id}/testemunhas`,
        {
          colaboradores_ids: testemunhas.map((colaborador) => colaborador.id),
          motivo,
        },
      );
      enqueueSnackbar(response.data.message, { variant: 'success' });
      fechar();
      onSalvo();
    } catch (e) {
      enqueueSnackbar(
        (e.response && e.response.data && e.response.data.message) ||
          'Erro ao definir as testemunhas.',
        { variant: 'error' },
      );
    } finally {
      salvandoRef.current = false;
      setSalvando(false);
    }
  }

  const d = documento || {};
  const total = testemunhas.length;
  const valido = total >= 2 && total <= 5;

  return (
    <Dialog open={Boolean(documento)} onClose={fechar} fullWidth maxWidth="sm">
      <Box
        display="flex"
        alignItems="center"
        px={2}
        py={1}
        style={{ backgroundColor: PRIMARY_COLOR, color: 'white' }}
      >
        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
          Assinar por testemunhas
        </Typography>
        <Box flex={1} />
        <IconButton
          size="small"
          onClick={fechar}
          disabled={salvando}
          style={{ color: 'white' }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent>
        <Typography variant="body2" color="textSecondary">
          {`${(d.modelo && d.modelo.nome) || 'Documento'} · ${
            (d.colaborador && d.colaborador.nome) || '—'
          }`}
        </Typography>
        <Alert severity="info" style={{ margin: '12px 0' }}>
          O colaborador deixará de assinar. Cada testemunha receberá o documento
          em Minhas pendências, com os dados dele, e precisará abrir e assinar.
          O documento só é concluído quando todas assinarem. Se o documento tem
          outras pessoas fixas para assinar, as assinaturas delas continuam
          valendo.
        </Alert>
        <Autocomplete
          multiple
          options={opcoes}
          value={testemunhas}
          getOptionLabel={(option) => option.nome}
          getOptionSelected={(option, value) => option.id === value.id}
          getOptionDisabled={(option) =>
            testemunhas.length >= 5 &&
            !testemunhas.some((item) => item.id === option.id)
          }
          onChange={(event, valor) => setTestemunhas(valor)}
          onInputChange={(event, valor) => setTermo(valor)}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label="Testemunhas (colaboradores)"
              helperText={`${total} escolhida(s) · de 2 a 5`}
              autoFocus
            />
          )}
        />
        <TextField
          fullWidth
          multiline
          rows={2}
          variant="outlined"
          label="Motivo"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          inputProps={{ maxLength: 500 }}
          style={{ marginTop: 16 }}
        />
      </DialogContent>
      <DialogActions>
        <Button
          color="primary"
          variant="contained"
          startIcon={<GroupAddIcon />}
          disabled={!valido || !motivo.trim() || salvando}
          onClick={handleSalvar}
        >
          {salvando ? 'Salvando...' : 'Confirmar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogoTestemunhas;
